<script>
import Layout from "@/router/layouts/main";
import draggable from "vuedraggable";
import { api } from "@/state/services";
import AdesaoGateModal from "./components/AdesaoGateModal.vue";
import LeadDetailModal from "./components/LeadDetailModal.vue";
import EmptyState from "@/components/widgets/empty-state";

const STATUSES = [
  { key: "Novo Lead",                 color: "#4e73df" },
  { key: "Contato Realizado",         color: "#36b9cc" },
  { key: "Reagendar Contato",         color: "#f6c23e" },
  { key: "Em Negociação",             color: "#6f42c1" },
  { key: "Lead Perdido",              color: "#e74a3b" },
  { key: "Aguardando Retorno",        color: "#858796" },
  { key: "Documentação Pendente",     color: "#fd7e14" },
  { key: "Cadastro/Proposta Enviada", color: "#20c9a6" },
  { key: "Contrato Gerado",           color: "#6610f2" },
  { key: "Venda Fechada",             color: "#1cc88a" },
];

// Lowercase + strip accents, so "joão" matches a search for "joao"
function normalizeSearch(str) {
  return (str || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export default {
  components: { Layout, draggable, AdesaoGateModal, LeadDetailModal, EmptyState },
  data() {
    return {
      loading: false,
      loadError: false,
      leads: [],
      search: "",
      columns: STATUSES.map((s) => ({ ...s, leads: [] })),
      viewMode: "kanban",
      updatingStatusId: null,
      statuses: STATUSES,
      detailVisible: false,
      selectedLead: null,
      fields: [
        { key: "afiliado_name",     label: "Afiliado" },
        { key: "name",              label: "Lead" },
        { key: "contact",           label: "Contato" },
        { key: "vehicle",           label: "Veículo" },
        { key: "consultant_status", label: "Status" },
        { key: "date_added",        label: "Data" },
        { key: "actions",           label: "" },
      ],
    };
  },
  computed: {
    filteredLeads() {
      const q = normalizeSearch(this.search);
      if (!q) return this.leads;
      return this.leads.filter((lead) => {
        const haystack = normalizeSearch(
          [
            lead.name,
            lead.email,
            lead.cellphone,
            lead.vehicle_type,
            lead.vehicle_brand,
            lead.vehicle_model,
            lead.vehicle_plate,
          ]
            .filter(Boolean)
            .join(" ")
        );
        return haystack.includes(q);
      });
    },
  },
  watch: {
    search() {
      this.populateColumns();
    },
  },
  created() {
    this.loadViewMode();
    this.load();
  },
  methods: {
    loadViewMode() {
      api.get("afiliados/settings").then((r) => {
        if (r.data.status === "success") {
          this.viewMode = r.data.settings.leads_view || "kanban";
        }
      });
    },
    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      api.put("afiliados/settings", { leads_view: mode });
    },
    openDetail(lead) {
      this.selectedLead  = lead;
      this.detailVisible = true;
    },
    onLeadSaved(updated) {
      const idx = this.leads.findIndex((l) => l.id === updated.id);
      if (idx !== -1) Object.assign(this.leads[idx], updated);
    },
    load() {
      this.loading = true;
      this.loadError = false;
      api
        .get("afiliados/user-leads?status=pending")
        .then((r) => {
          if (r.data.status === "success") {
            this.leads = r.data.list;
            this.populateColumns();
          } else {
            this.loadError = true;
          }
        })
        .catch(() => { this.loadError = true; })
        .finally(() => { this.loading = false; });
    },

    populateColumns() {
      this.columns.forEach((col) => { col.leads = []; });
      this.filteredLeads.forEach((lead) => {
        const status = lead.consultant_status || "Novo Lead";
        const col = this.columns.find((c) => c.key === status) || this.columns[0];
        col.leads.push(lead);
      });
    },

    // Called by the kanban draggable — only acts on the "added" side of the transfer
    onDrop(column, event) {
      if (!event.added) return;
      const lead = event.added.element;
      const newStatus = column.key;
      const oldStatus = lead.consultant_status || "Novo Lead";
      if (oldStatus === newStatus) return;

      // Optimistic update in the flat array
      lead.consultant_status = newStatus;
      const idx = this.leads.findIndex((l) => l.id === lead.id);
      if (idx !== -1) this.leads[idx].consultant_status = newStatus;

      this.updatingStatusId = lead.id;
      api
        .put(`afiliados/leads/${lead.id}/status`, { consultant_status: newStatus })
        .then((r) => {
          if (r.data.status !== "success") {
            this.revertLead(lead, idx, oldStatus);
          }
        })
        .catch(() => { this.revertLead(lead, idx, oldStatus); })
        .finally(() => { this.updatingStatusId = null; });
    },

    revertLead(lead, idx, oldStatus) {
      lead.consultant_status = oldStatus;
      if (idx !== -1) this.leads[idx].consultant_status = oldStatus;
      this.populateColumns();
    },

    // Used only by the list-view status select
    updateStatus(lead, newStatus) {
      if (this.updatingStatusId === lead.id) return;
      const oldStatus = lead.consultant_status;
      lead.consultant_status = newStatus;
      this.updatingStatusId = lead.id;
      api
        .put(`afiliados/leads/${lead.id}/status`, { consultant_status: newStatus })
        .then((r) => {
          if (r.data.status !== "success") {
            lead.consultant_status = oldStatus;
          }
          this.populateColumns();
        })
        .catch(() => { lead.consultant_status = oldStatus; })
        .finally(() => { this.updatingStatusId = null; });
    },

    openWhatsApp(lead) {
      const phone = (lead.cellphone || "").replace(/\D/g, "");
      if (!phone) return;
      const full = phone.startsWith("55") ? phone : `55${phone}`;
      window.open(`https://wa.me/${full}`, "_blank");
    },

    vehicleLabel(lead) {
      return (
        [lead.vehicle_type, lead.vehicle_brand, lead.vehicle_model, lead.vehicle_year]
          .filter(Boolean)
          .join(" ") || "—"
      );
    },

    formatDate(d) {
      return d ? new Date(d).toLocaleDateString("pt-BR") : "—";
    },

    statusColor(key) {
      const s = STATUSES.find((x) => x.key === key);
      return s ? s.color : "#858796";
    },
  },
};
</script>

<template>
  <Layout>
    <AdesaoGateModal />

    <!-- Page header -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap" style="gap:.5rem;">
      <div>
        <h3 class="mb-0">Leads Indicados</h3>
        <span class="badge badge-soft-primary font-size-12">{{ leads.length }} pendente(s)</span>
      </div>
      <div class="btn-group" role="group">
        <button
          type="button"
          :class="['btn btn-sm', viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary']"
          @click="setViewMode('list')"
        >
          <i class="mdi mdi-format-list-bulleted mr-1"></i> Lista
        </button>
        <button
          type="button"
          :class="['btn btn-sm', viewMode === 'kanban' ? 'btn-primary' : 'btn-outline-primary']"
          @click="setViewMode('kanban')"
        >
          <i class="mdi mdi-view-column mr-1"></i> Kanban
        </button>
      </div>
    </div>

    <!-- Busca -->
    <div class="mb-4">
      <b-input-group style="max-width:420px;">
        <b-input-group-prepend is-text>
          <i class="mdi mdi-magnify"></i>
        </b-input-group-prepend>
        <b-form-input
          v-model="search"
          placeholder="Buscar por nome, contato ou veículo..."
        ></b-form-input>
        <b-input-group-append v-if="search" is-text>
          <i class="mdi mdi-close cursor-pointer" @click="search = ''"></i>
        </b-input-group-append>
      </b-input-group>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="text-center py-5 text-danger">
      <i class="mdi mdi-alert-circle-outline font-size-48 d-block mb-2"></i>
      Erro ao carregar leads. Tente recarregar a página.
    </div>

    <!-- Empty -->
    <div v-else-if="leads.length === 0" class="card">
      <div class="card-body">
        <EmptyState
          icon="bx bx-user-plus"
          title="Nenhum lead pendente no momento"
          text="Assim que seus afiliados indicarem alguém, os leads aparecem aqui para você trabalhar."
        />
      </div>
    </div>

    <!-- Sem resultados na busca -->
    <div v-else-if="filteredLeads.length === 0" class="card">
      <div class="card-body">
        <EmptyState
          icon="bx bx-search-alt"
          title="Nenhum resultado para a sua busca"
          :text="'Não encontramos leads para \'' + search + '\'. Tente outro nome, telefone ou placa.'"
        />
      </div>
    </div>

    <!-- ===== LIST VIEW ===== -->
    <div v-else-if="viewMode === 'list'" class="card">
      <div class="card-body">
        <div class="table-responsive">
          <b-table stacked="md" :items="filteredLeads" :fields="fields" hover responsive class="mb-0">
            <template #cell(afiliado_name)="{ item }">
              <span class="font-weight-medium">{{ item.afiliado_name }}</span>
            </template>

            <template #cell(name)="{ item }">
              <div class="font-weight-medium">{{ item.name }}</div>
              <small class="text-muted">{{ item.cpf }}</small>
            </template>

            <template #cell(contact)="{ item }">
              <div>{{ item.email || '—' }}</div>
              <small class="text-muted">{{ item.cellphone }}</small>
            </template>

            <template #cell(vehicle)="{ item }">
              <span class="text-capitalize">{{ vehicleLabel(item) }}</span>
              <div v-if="item.vehicle_plate" class="text-muted small">{{ item.vehicle_plate }}</div>
            </template>

            <template #cell(consultant_status)="{ item }">
              <select
                :value="item.consultant_status || 'Novo Lead'"
                :disabled="updatingStatusId === item.id"
                class="form-control form-control-sm status-select"
                :style="{ borderLeftColor: statusColor(item.consultant_status || 'Novo Lead') }"
                @change="updateStatus(item, $event.target.value)"
              >
                <option v-for="s in statuses" :key="s.key" :value="s.key">{{ s.key }}</option>
              </select>
            </template>

            <template #cell(date_added)="{ item }">
              {{ formatDate(item.date_added) }}
            </template>

            <template #cell(actions)="{ item }">
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-1"
                title="Ver detalhes"
                @click="openDetail(item)"
              >
                <i class="mdi mdi-eye-outline"></i>
              </b-button>
              <b-button
                size="sm"
                variant="success"
                :disabled="!item.cellphone"
                :title="item.cellphone ? 'WhatsApp: ' + item.cellphone : 'Sem telefone'"
                @click="openWhatsApp(item)"
              >
                <i class="mdi mdi-whatsapp"></i>
              </b-button>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <!-- ===== KANBAN VIEW ===== -->
    <div v-else class="kanban-board">
      <div v-for="col in columns" :key="col.key" class="kanban-column">
        <!-- Column header -->
        <div class="kanban-col-header" :style="{ borderTopColor: col.color }">
          <span class="font-weight-bold" style="font-size:.8rem;">{{ col.key }}</span>
          <span class="badge badge-light ml-1" style="font-size:.75rem;">{{ col.leads.length }}</span>
        </div>

        <!-- Draggable card list — empty placeholder must live OUTSIDE <draggable>
             so SortableJS doesn't count it as a draggable item -->
        <div class="kanban-col-body">
          <draggable
            :list="col.leads"
            group="leads"
            :animation="150"
            ghost-class="kanban-card--ghost"
            drag-class="kanban-card--drag"
            tag="div"
            class="kanban-dropzone"
            @change="(e) => onDrop(col, e)"
          >
            <div
              v-for="lead in col.leads"
              :key="lead.id"
              class="kanban-card"
              :class="{ 'kanban-card--saving': updatingStatusId === lead.id }"
            >
            <!-- Drag handle + name + WhatsApp -->
            <div class="d-flex align-items-start">
              <i class="mdi mdi-drag-vertical kanban-drag-handle mr-1"></i>
              <div class="flex-grow-1" style="min-width:0;">
                <div class="font-weight-bold kanban-lead-name">{{ lead.name }}</div>
                <div class="kanban-meta" v-if="lead.cpf">{{ lead.cpf }}</div>
              </div>
              <button
                class="btn btn-sm p-0 ml-1 text-secondary flex-shrink-0 kanban-wa-btn"
                title="Ver detalhes"
                @click.stop="openDetail(lead)"
              >
                <i class="mdi mdi-eye-outline"></i>
              </button>
              <button
                v-if="lead.cellphone"
                class="btn btn-sm p-0 ml-1 text-success flex-shrink-0 kanban-wa-btn"
                :title="'WhatsApp: ' + lead.cellphone"
                @click.stop="openWhatsApp(lead)"
              >
                <i class="mdi mdi-whatsapp"></i>
              </button>
            </div>

            <!-- Contact -->
            <div class="kanban-meta mt-1" v-if="lead.cellphone">
              <i class="mdi mdi-phone-outline"></i> {{ lead.cellphone }}
            </div>

            <!-- Vehicle -->
            <div
              v-if="vehicleLabel(lead) !== '—'"
              class="kanban-meta text-capitalize"
            >
              <i class="mdi mdi-car-outline"></i> {{ vehicleLabel(lead) }}
            </div>

            <!-- Affiliate -->
            <div class="kanban-meta">
              <i class="mdi mdi-account-outline"></i> {{ lead.afiliado_name }}
            </div>

            <!-- Saving spinner -->
            <div v-if="updatingStatusId === lead.id" class="kanban-saving-overlay">
              <b-spinner small variant="secondary"></b-spinner>
            </div>
          </div>
          </draggable>

          <!-- Empty state is outside <draggable> so SortableJS ignores it -->
          <div v-if="col.leads.length === 0" class="kanban-empty">
            <i class="mdi mdi-tray-arrow-down"></i>
          </div>
        </div>
      </div>
    </div>

    <LeadDetailModal
      v-model="detailVisible"
      :lead="selectedLead"
      :editable="true"
      @saved="onLeadSaved"
    />
  </Layout>
</template>

<style scoped>
/* ── Kanban board ─────────────────────────────────────── */
.kanban-board {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 16px;
  align-items: flex-start;
}

.kanban-column {
  flex: 0 0 215px;
  background: var(--st-canvas);
  border-radius: var(--st-radius-sm);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Kanban no celular ───────────────────────────────────
   Aqui a rolagem horizontal é PROPOSITAL e continua: o quadro é de
   arrastar-e-soltar entre colunas do funil, e empilhar as colunas
   destruiria a metáfora. O que muda é a forma de navegar — em vez de
   quatro colunas de 215px espremidas e cortadas ao meio, cada coluna
   passa a ocupar quase a tela inteira e o `scroll-snap` para exatamente
   nela, como as páginas de um carrossel. Os 14% que sobram deixam a
   próxima coluna espiando, que é o que sinaliza que há mais conteúdo. */
@media (max-width: 767.98px) {
  .kanban-board {
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 12px;
  }

  .kanban-column {
    flex: 0 0 86%;
    scroll-snap-align: start;
  }

  /* Sem altura fixa: a coluna cresce e quem rola é a página, não um
     segundo contêiner rolável dentro dela (rolagem aninhada no dedo é
     imprevisível). */
  .kanban-col-body {
    max-height: none;
    overflow-y: visible;
  }

  /* Com a coluna larga, o nome do lead cabe em duas linhas e não precisa
     mais ser cortado com reticências. */
  .kanban-lead-name,
  .kanban-meta {
    white-space: normal;
    overflow: visible;
    overflow-wrap: anywhere;
  }
}

.kanban-col-header {
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--st-surface);
  border-top: 3px solid var(--st-border);
  border-bottom: 1px solid var(--st-border-soft);
}

.kanban-col-body {
  padding: 8px;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  flex: 1;
}

/* The draggable zone needs min-height so empty columns still accept drops */
.kanban-dropzone {
  min-height: 56px;
}

/* ── Cards ────────────────────────────────────────────── */
.kanban-card {
  position: relative;
  background: var(--st-surface);
  border-radius: var(--st-radius-sm);
  padding: 8px 10px;
  margin-bottom: 8px;
  box-shadow: var(--st-shadow-xs);
  cursor: grab;
  user-select: none;
}

.kanban-card:active {
  cursor: grabbing;
}

.kanban-lead-name {
  font-size: .82rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kanban-meta {
  font-size: .72rem;
  color: var(--st-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kanban-drag-handle {
  color: var(--st-text-soft);
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 1px;
}

.kanban-wa-btn {
  font-size: 1.05rem;
  line-height: 1;
}

/* Empty column placeholder */
.kanban-empty {
  text-align: center;
  color: var(--st-text-muted);
  font-size: 1.4rem;
  padding: 16px 0;
  pointer-events: none; /* don't block drops */
}

/* Saving overlay */
.kanban-card--saving {
  opacity: 0.6;
  pointer-events: none;
}

.kanban-saving-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--st-radius-sm);
}

/* Dragging states */
.kanban-card--ghost {
  opacity: 0.35;
  background: var(--st-brand-soft);
  border: 2px dashed var(--st-brand);
}

.kanban-card--drag {
  box-shadow: var(--st-shadow-md);
  opacity: 0.95;
  cursor: grabbing;
}

/* ── List view status select ──────────────────────────── */
.status-select {
  border-left-width: 3px;
  border-left-style: solid;
  min-width: 175px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
